// DiscountDetailsPanel.tsx
import React, { useState, useEffect, useMemo, act } from 'react';
import Toggle from '../../components/common/Toggle' 
import './pricingPanel.css';
import PanelEditableCell from './panelEditableCell';
import ConfirmDialog from '../../components/common/WarningBox';
import Dropdown from '../../components/common/dropdown/dropdown';
import { useApiData} from '../../contexts/apiService';
import { useDataManager } from '../../contexts/dataManager';
import { LeaseCoordinateItem } from '../../contexts/tableDefinitions';

interface DiscountDetailsPanelProps {
	discount: any;
	allDiscounts: any[];
	onClose: () => void;
    handleEditOrder: (table_name: string, id: string | number, edited_col: string, old_value: string | number, new_value: string | number) => Promise<boolean>;
	handleDeleteRow: (id: Set<string>, table_name: string) => Promise<boolean>;
	handleAddRow: (tableName: string, rowData: Record<string, string>, loadAmount?: number) => Promise<boolean>;
	activeTable: string;
}

interface DropdownOption {
    value: string;
    label: string;
}

const TICKET_FIELDS = [
    'cmat',
    'cmaa',
	'cma_diff',
    'mid_cush_diff',
    'wtl',
	'dapl_jc',
	'argus_guernsey',
    'nymex_2/3_1/3',
    'actual_roll',
    'plains_wti',
    'wts',
    'argus_p',
    'hls',
	'meh',
	'wti_wtl_pricing',
];

const DiscountDetailsPanel: React.FC<DiscountDetailsPanelProps> = ({
    discount,
    allDiscounts,
    onClose,
    // handleEditOrder,
    handleDeleteRow,
    handleAddRow,
	activeTable,
}) => {

	const { apiData, handleEditOrder } = useApiData();
	const { requestTableData, hasTableData } = useDataManager();
	const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(false);

	const [deductValue, setDeductValue] = useState<string>("");
	const [oldDeductValue, setOldDeductValue] = useState<string>("");
	const [stationCharge, setStationCharge] = useState<string>("");
	const [oldStationCharge, setOldStationCharge] = useState<string>("");

    const [bswEnabled, setBswEnabled] = useState(false);
    const [gravEnabled, setGravEnabled] = useState(false);
	const [nglEnabled, setNglEnabled] = useState(false);
	const [isItemActive, setIsItemActive] = useState(true);
	const [isSplitDeal, setIsSplitDeal] = useState(false);
	const [isPickedUpDeal, setIsPickedUpDeal] = useState(false);
	const [isBuySellDeal, setIsBuySellDeal] = useState(false);
	const [buySellComponents, setBuySellComponents] = useState(false);
	
	const [isSplitTrucking, setIsSplitTrucking] = useState(false);
	const [isSplitAllLeases, setIsSplitAllLeases] = useState(false);
	const [isSplitAllStations, setIsSplitAllStations] = useState(false);
	const [isRockbridgeTitle, setIsRockbridgeTitle] = useState(false);

	const [activePricing, setActivePricing] = useState(discount);
	const [monthYearOptions, setMonthYearOptions] = useState<string[]>([]);
	const [currentMonthYear, setCurrentMonthYear] = useState<string>("");
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
	const [dropdownSelection, setDropdownSelection] = useState('');

	const [location1Options, setLocation1Options] = useState<{ value: string, label: string }[]>([]);
	const [selectedLocation1, setSelectedLocation1] = useState('');
	const [location2Options, setLocation2Options] = useState<{ value: string, label: string }[]>([]);
	const [selectedLocation2, setSelectedLocation2] = useState('');

	const [leaseOptions, setLeaseOptions] = useState<{ value: string, label: string }[]>([]);
	const [marketOptions, setMarketOptions] = useState<{ value: string, label: string }[]>([]);
	const [stationOptions, setStationOptions] = useState<{ value: string, label: string }[]>([]);
	const [entityOptions, setEntityOptions] = useState<{ value: string, label: string }[]>([]);

	const [selectedSplitStation, setSelectedSplitStation] = useState('');

	const [selectedLocation, setSelectedLocation] = useState('');
	const [pickupDealLocation, setPickupDealLocation] = useState('');

	const [locationPricingRows, setLocationPricingRows] = useState<any[]>([]);

	// initial pricing on selection change
	useEffect(() => {
		if (activePricing === null) {
			setActivePricing(discount);
		}
		const month = discount.month || "";
		const year = discount.year || "";
		setCurrentMonthYear(month && year ? `${month} ${year}` : "");

		if (activeTable == 'producer_pricing') {
			setDropdownOptions([
				{ value: 'lease', label: 'Lease' },
				{ value: 'market', label: 'Market' },
				{ value: 'entity', label: 'Entity' },
			]);
		}
		if (activeTable == 'market_discounts') {
			setDropdownOptions([
				{ value: 'producer', label: 'Producer' },
				{ value: 'offload_lane', label: 'Offload Lane' },
				{ value: 'product_type', label: 'Product Type' },
			]);
		}

		setDropdownSelection('')

	}, [discount, activeTable]);

	// useEffect to track live changes
	useEffect(() => {
		// Find the current selected pricing data in the updated allDiscounts
		if (currentMonthYear) {
			const [selectedMonth, selectedYear] = currentMonthYear.split(" ");
			
			// Find the matching pricing data with updated values
			const updatedPricing = allDiscounts.find(d => {
				if (activeTable === 'market_discounts') {
					return (
						d.station_owner === discount.station_owner &&
						d.station === discount.station &&
						d.market === discount.market &&
						d.month === selectedMonth &&
						String(d.year) === selectedYear &&
						d.main === 1
					);
				} else if (activeTable === 'producer_pricing') {
					return (
						d.producer === discount.producer &&
						d.month === selectedMonth &&
						String(d.year) === selectedYear &&
						d.main === 1
					);
				}
				return false;
			});
			
			// Update the active pricing if found
			if (updatedPricing) {
				setActivePricing(updatedPricing);
			}
		} else {
			// If no month/year is selected, use the default discount
			setActivePricing(discount);
		}
	}, [allDiscounts, discount, currentMonthYear, activeTable]);

	// getting month/year data for dropdown options
	useEffect(() => {
		const relevantDiscounts = allDiscounts.filter(d => {
			if (activeTable === 'market_discounts') {
				return (
					d.station_owner === discount.station_owner &&
					d.station === discount.station &&
					d.market === discount.market &&
					d.main === 1
				);
			} else if (activeTable === 'producer_pricing') {
				return (
					d.producer === discount.producer &&
				 	d.main === 1
				);
			}
			return false;
		});
		
		// Array to hold all month-year strings
		const monthYearStrings = relevantDiscounts
			.filter(d => d.month && d.year)
			.map(d => `${d.month} ${d.year}`);
	  
		// Remove duplicates by converting to Set and back to Array
		const uniqueMonthYears = Array.from(new Set(monthYearStrings));
		
		// Month name to number mapping for sorting
		const monthToNumber = {
			'January': 1,
			'February': 2,
			'March': 3,
			'April': 4,
			'May': 5,
			'June': 6,
			'July': 7,
			'August': 8,
			'September': 9,
			'October': 10,
			'November': 11,
			'December': 12
		};
	  
		// Sort in descending order (newest first)
		const sortedMonthYears = uniqueMonthYears.sort((a, b) => {
			const [monthA, yearA] = a.split(' ');
			const [monthB, yearB] = b.split(' ');
			
			// First compare years
			const yearDiff = parseInt(yearB) - parseInt(yearA);
			if (yearDiff !== 0) {
			return yearDiff;
			}
			
			// If years are equal, compare months
			return monthToNumber[monthB as keyof typeof monthToNumber] - monthToNumber[monthA as keyof typeof monthToNumber];
		});

		setMonthYearOptions(sortedMonthYears);
	}, [allDiscounts, discount, activeTable]);

	// function to handle month/year dropdown changes
	const handleMonthYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedMonthYear = e.target.value;
  		setCurrentMonthYear(selectedMonthYear);

		if (selectedMonthYear) {
			const [selectedMonth, selectedYear] = selectedMonthYear.split(" ");

			// Find pricing data for the selected month/year
			const selectedPricing = allDiscounts.find(d => {
				if (activeTable === 'market_discounts') {
					return (
						d.station_owner === discount.station_owner &&
						d.station === discount.station &&
						d.market === discount.market &&
						d.month === selectedMonth &&
						String(d.year) === selectedYear &&
						d.main === 1
					);
				} else if (activeTable === 'producer_pricing') {
					return (
						d.producer === discount.producer &&
						d.month === selectedMonth &&
						String(d.year) === selectedYear &&
						d.main === 1
					);
				}
				return false;
			});

			if (selectedPricing) {
				setActivePricing(selectedPricing);
			}
		}
	};

	const handleCheckboxSave = async (fieldName: string, currentValue: boolean) => {
		setIsLoading(true);
		try {
			// Save checkbox changes
			const oldValueString = currentValue ? '1' : '0';
			const newValueString = !currentValue ? '1' : '0';
			
			await handleEditOrder(activeTable, activePricing.id, fieldName, oldValueString, newValueString);
			
		} catch (error) {
			console.error('Error saving changes:', error);
		} finally {
			setIsLoading(false);
		}
	};

	// setting initial values
    useEffect(() => {
		const initialValues: Record<string, boolean> = {};
		TICKET_FIELDS.forEach(field => {
			initialValues[field] =
			activePricing[field] === 1;
		});
		setCheckboxValues(initialValues);
		setDeductValue(activePricing.discount);
		setOldDeductValue(activePricing.discount)
		setStationCharge(activePricing.station_charge);
		setOldStationCharge(activePricing.station_charge)

		setBswEnabled(activePricing.bsw === 1);
		setGravEnabled(activePricing.gravity === 1);
		setNglEnabled(activePricing.ngl === 1);
		setIsItemActive(activePricing.is_active == null || activePricing.is_active === 1);
		setIsPickedUpDeal(activePricing.is_pickedup_deal === 1);
		
		setIsBuySellDeal(activePricing.is_buy_sell_deal === 1);
		setBuySellComponents(activePricing.buy_sell_components === 1);
		setIsSplitDeal(activePricing.is_split_deal === 1);
		setIsSplitTrucking(activePricing.split_trucking === 1);
		setIsSplitAllLeases(activePricing.split_all_leases === 1);
		setIsSplitAllStations(activePricing.split_all_stations === 1);
		setIsRockbridgeTitle(activePricing.rockbridge_title === 1);

		setPickupDealLocation(activePricing.pickedup_deal_at);

    }, [activePricing, discount]);
  
    const handleCheckboxChange = (field: string) => {
		setCheckboxValues(prev => ({
			...prev,
			[field]: !prev[field]
		}));
    };
  
    const handleBswChange = () => {
      	setBswEnabled(prev => !prev);
    };
  
    const handleGravChange = () => {
      	setGravEnabled(prev => !prev);
    };
	
	const handleNglChange = () => {
		setNglEnabled(prev => !prev);
	};
	// useEffect to update location deducts
	useEffect(() => {
		if (!activePricing || !activePricing.month || !activePricing.year) return;
		
		if (activeTable === 'producer_pricing') {
			// Filter rows based on location_pricing flag
			const filteredRows = allDiscounts.filter(row => {
				// Basic match criteria for location pricing
				const basicMatch = 
					row.producer === activePricing.producer &&
					row.month === activePricing.month &&
					String(row.year) === String(activePricing.year) &&
					row.main === 0 &&
					row.location_pricing === 1;
				
				return basicMatch;
			});
			
			setLocationPricingRows(filteredRows);
		}

		if (activeTable === 'market_discounts') {
			// Filter rows based on location_pricing flag
			const filteredRows = allDiscounts.filter(row => {
				// Basic match criteria for location pricing
				const basicMatch = 
					row.station_owner === activePricing.station_owner &&
					row.station === activePricing.station &&
					row.market === activePricing.market &&
					row.month === activePricing.month &&
					String(row.year) === String(activePricing.year) &&
					row.main === 0 &&
					row.location_pricing === 1;
				
				return basicMatch;
			});
			
			setLocationPricingRows(filteredRows);
		}
	}, [allDiscounts, activePricing, activeTable]);

	// useEffect to update producer_pricing dropdowns
	useEffect(() => {
		if (apiData?.lease_coordinates && apiData?.market_coordinates && activeTable === 'producer_pricing' && (dropdownSelection || !isSplitAllStations)) {
			
			let filteredLeases: LeaseCoordinateItem[] = [];

			if (dropdownSelection === 'lease') {
				filteredLeases = apiData?.lease_coordinates.filter(
					lease => lease.producer === discount.producer
				);

				// Extract unique lease names and create dropdown options
				const uniqueLeases = new Set(filteredLeases.map(lease => lease.lease));
				const leaseDropdownOptions = Array.from(uniqueLeases)
					.filter(lease => lease) // Filter out null/empty values
					.sort() // Sort alphabetically
					.map(lease => ({
						value: lease,
						label: lease
					}));
				
				setLeaseOptions(leaseDropdownOptions);

				// Reset selected lease if not in the new options
				if (leaseDropdownOptions.length > 0 && !leaseDropdownOptions.some(option => option.value === selectedLocation)) {
					setSelectedLocation('');
				}
			}

			if (dropdownSelection === 'entity') {
				const filteredEntities = apiData?.lease_coordinates.filter(
					entity => entity.producer === discount.producer
				);

				// Extract unique entity names and create dropdown options
				const uniqueEntities = new Set(filteredEntities.map(entity => entity.entity));
				const entityDropdownOptions = Array.from(uniqueEntities)
					.filter(entity => entity) // Filter out null/empty values
					.sort() // Sort alphabetically
					.map(entity => ({
						value: entity,
						label: entity
					}));
				
				setEntityOptions(entityDropdownOptions);

				// Reset selected entity if not in the new options
				if (entityDropdownOptions.length > 0 && !entityDropdownOptions.some(option => option.value === selectedLocation)) {
					setSelectedLocation('');
				}
			}

			if (dropdownSelection === 'market') {
				// Extract unique market names and create dropdown options
				const uniqueMarket = new Set(apiData?.market_coordinates.map(market => market.market));

				const marketDropdownOptions = Array.from(uniqueMarket)
					.filter(market => market) // Filter out null/empty values
					.sort() // Sort alphabetically
					.map(market => ({
						value: market,
						label: market
					}));
			
				setMarketOptions(marketDropdownOptions);
				
				// Reset selected market if not in the new options
				if (marketDropdownOptions.length > 0 && !marketDropdownOptions.some(option => option.value === selectedLocation)) {
					setSelectedLocation('');
				}
			}

			if (!isSplitAllStations) {
				// Extract unique station names and create dropdown options
				const uniqueStation = new Set(apiData?.market_coordinates.map(station => station.station));

				const stationDropdownOptions = Array.from(uniqueStation)
					.filter(station => station) // Filter out null/empty values
					.sort() // Sort alphabetically
					.map(station => ({
						value: station,
						label: station
					}));
			
				setStationOptions(stationDropdownOptions);
			}
		}
	}, [apiData?.market_coordinates, apiData?.lease_coordinates, dropdownSelection, discount, activeTable, isSplitAllStations]);

	// useEffect to update market_discounts dropdowns
	useEffect(() => {
		if (apiData?.lease_coordinates && apiData?.market_coordinates && activeTable === 'market_discounts' && dropdownSelection) {
			
			if (dropdownSelection == 'producer') {
				
				setSelectedLocation2('')

				const uniqueProducers = new Set(apiData?.lease_coordinates.map(producer => producer.producer));
				const producerDropdownOptions = Array.from(uniqueProducers)
					.filter(producer => producer) // Filter out null/empty values
					.sort() // Sort alphabetically
					.map(producer => ({
						value: producer,
						label: producer
					}));
			
				setLocation1Options(producerDropdownOptions);

				if (selectedLocation1) {

					const filteredLeases = apiData?.lease_coordinates.filter(
						lease => lease.producer === selectedLocation1
					);
				
					// Extract unique lease names and create dropdown options
					const uniqueLeases = new Set(filteredLeases.map(lease => lease.lease));
					const leaseDropdownOptions = Array.from(uniqueLeases)
						.filter(lease => lease) // Filter out null/empty values
						.sort() // Sort alphabetically
						.map(lease => ({
							value: lease,
							label: lease
						}));
					
					setLocation2Options(leaseDropdownOptions);
				}
			}

			if (dropdownSelection == 'offload_lane') {
				
				setSelectedLocation2('')

				const filteredData = apiData.market_coordinates.filter(item => 
					item.market === discount.station_owner && 
					item.station === discount.station
				);
		
				// Group by unique market (offload lane)
				const uniqueOffloadLanes = new Set<string>();
				
				// Process each item
				filteredData.forEach(item => {
					if (item.offload_lanes) {
						// Split the comma-separated string and trim each value
						const lanes = item.offload_lanes.split(',').map(lane => lane.trim());
						
						// Add each individual lane to the set
						lanes.forEach(lane => {
							if (lane) uniqueOffloadLanes.add(lane);
						});
					}
				});
				
				// Convert to dropdown options format
				const offloadLaneOptions = Array.from(uniqueOffloadLanes)
					.map(lane => ({
						value: lane,
						label: lane
					}))
					.sort((a, b) => a.label.localeCompare(b.label));
				
				setLocation1Options(offloadLaneOptions);
			}

			if (dropdownSelection == 'product_type') {
				
				setSelectedLocation2('')
		
				// Group by unique product type
				const uniqueProductTypes = new Set<string>();
				apiData.lease_coordinates.forEach(item => {
					if (item.product_type) {
						uniqueProductTypes.add(item.product_type);
					}
				});
				
				// Convert to dropdown options format
				const productTypeOptions = Array.from(uniqueProductTypes)
					.map(productType => ({
						value: productType,
						label: productType
					}))
					.sort((a, b) => a.label.localeCompare(b.label));
			
				setLocation1Options(productTypeOptions);
			}
		}
	}, [apiData?.market_coordinates, apiData?.lease_coordinates, discount, activeTable, dropdownSelection, selectedLocation1]);

    const handleSave = async () => {
		setIsLoading(true);
		try {
			const editPromises: Promise<boolean>[] = [];

			// Save Deduct change
			if (deductValue !== oldDeductValue) {
				editPromises.push(
					handleEditOrder(activeTable, activePricing.id, 'discount', oldDeductValue, deductValue)
				);
			}
			
			// Save Deduct change
			if (stationCharge !== oldStationCharge) {
				editPromises.push(
					handleEditOrder(activeTable, activePricing.id, 'station_charge', oldStationCharge, stationCharge)
				);
			}

			// Save checkbox changes
			const ticketPromises = Object.entries(checkboxValues).map(([field, newValue]) => {
				const oldValue = activePricing[field]?.toString() || '0';
				const newValueString = newValue ? '1' : '0';
				if (oldValue !== newValueString) {
					return handleEditOrder(activeTable, activePricing.id, field, oldValue, newValueString);
				}
				return Promise.resolve();
			});
  
			// Save BSW flag change
			const oldBswValue = activePricing.bsw?.toString() || '0';
			const newBswValue = bswEnabled ? '1' : '0';
			if (oldBswValue !== newBswValue) {
				editPromises.push(
					handleEditOrder(activeTable, activePricing.id, 'bsw', oldBswValue, newBswValue)
				);
			}
  
			// Save Gravity flag change
			const oldGravValue = activePricing.gravity?.toString() || '0';
			const newGravValue = gravEnabled ? '1' : '0';
			if (oldGravValue !== newGravValue) {
				editPromises.push(
					handleEditOrder(activeTable, activePricing.id, 'gravity', oldGravValue, newGravValue)
				);
			}

			const oldNglValue = activePricing.ngl?.toString() || '0';
			const newNglValue = nglEnabled ? '1' : '0';
			if (oldNglValue !== newNglValue) {
				editPromises.push(
					handleEditOrder(activeTable, activePricing.id, 'ngl', oldNglValue, newNglValue)
				);
			}
  
        	const results = await Promise.all([...ticketPromises, ...editPromises]);

			const allSuccessful = results.every(result => result === true);
			
			if (allSuccessful) {
				console.log('All changes saved successfully');
				onClose();
			} else {
				console.error('Some changes failed to save');
			}

		} catch (error) {
			console.error('Error saving changes:', error);
		} finally {
			setIsLoading(false);
		}
    };

	const handleDeleteClick = () => {
		if (!activePricing || !activePricing.month || !activePricing.year) {
			console.error('No month/year selected for deletion');
			return;
		}
		
		// Open the confirmation dialog
		setIsDeleteDialogOpen(true);
	};

	const confirmDelete = async () => {
		setIsLoading(true);
		try {
			const rowsToDelete = allDiscounts.filter(row => {
				// Must match month and year
				const monthYearMatch = 
					row.month === activePricing.month && 
					String(row.year) === String(activePricing.year);
					
				if (!monthYearMatch) return false;
				
				// Must match the entity identifier (producer or station details)
				if (activeTable === 'producer_pricing') {
					return row.producer === activePricing.producer;
					
				} else if (activeTable === 'market_discounts') {
					return (
						row.station_owner === activePricing.station_owner &&
						row.station === activePricing.station &&
						row.market === activePricing.market
					);
				}
				return false;
			});
			
			// Extract IDs for deletion
			const idsToDelete = new Set(rowsToDelete.map(row => row.id));
			
			if (idsToDelete.size === 0) {
				console.warn('No matching rows found for deletion');
				setIsDeleteDialogOpen(false);
				return;
			}
			
			// Perform the deletion
			await handleDeleteRow(idsToDelete, activeTable);
			// Close the dialog and panel after successful deletion
			setIsDeleteDialogOpen(false);
			onClose();
		} catch (error) {
			console.error('Error deleting month:', error);
		} finally {
			setIsLoading(false);
		}
	};

    // DeductValueCell: displays and edits the "discount" value.
    const DeductValueCell: React.FC<{ bracket: any }> = ({ bracket }) => {
		const [value, setValue] = useState<string>(bracket.discount?.toString() || "");
	
		useEffect(() => {
			setValue(bracket.discount?.toString() || "");
		}, [bracket]);
  
		const handleValueSave = async (newValue: string) => {
			if (newValue !== value) {
				await handleEditOrder(
					activeTable,
					bracket.id,
					'discount',
					bracket.discount?.toString() || "",
					newValue
				);
				setValue(newValue);
			}
		};
      	return <PanelEditableCell initialValue={value} onSave={handleValueSave} />;
    };
  
    // DeductTypeCell: displays the Toggle for the discount type.
    const DeductTypeCell: React.FC<{ bracket: any }> = ({ bracket }) => {
		// Determine initial mode: true means percentage, based on numeric 1/0.
		const initialMode =
			bracket.disc_percentage === 1 || bracket.disc_percentage === '1';
		const [usePercentage, setUsePercentage] = useState<boolean>(initialMode);
  
		const handleToggleChange = async (checked: boolean) => {
			setUsePercentage(checked);
			await handleEditOrder(
				activeTable,
				bracket.id,
				'disc_percentage',
				usePercentage ? "1" : "0",
				checked ? "1" : "0"
			);
		};
  
		return (
			<Toggle
				checked={usePercentage}
				onChange={handleToggleChange}
				labelOn="Percentage"
				labelOff="Fixed"
			/>
		);
    };
  
    // Get editable BS&W brackets
    const bswBrackets = useMemo(() => {
		if (!bswEnabled) return [];

		const filterCondition = activeTable === 'market_discounts' 
        ? (row: any) => 
            row.station_owner === activePricing.station_owner &&
            row.station === activePricing.station &&
            row.market === activePricing.market &&
			row.month === activePricing.month &&
			row.year === activePricing.year &&
            row.main === 0 &&
            row.bsw === 1
        : activeTable === 'producer_pricing'
        ? (row: any) => 
            row.producer === activePricing.producer &&
			row.month === activePricing.month &&
			row.year === activePricing.year &&
            row.main === 0 &&
            row.bsw === 1
        : () => false;

		return allDiscounts
			.filter(filterCondition)
			.sort((a, b) => parseFloat(a.bsw_low) - parseFloat(b.bsw_low));
			
    }, [allDiscounts, discount, activePricing, bswEnabled, activeTable]);
  
    // Called when one of the editable cells (for bsw_low or bsw_high) is saved
    const handleEditBracket = async (bracket: any, field: string, newValue: string) => {
		const oldValue = bracket[field]?.toString() || '';
		if (oldValue !== newValue) {
			await handleEditOrder(activeTable, bracket.id, field, oldValue, newValue);
		}
    };
    
    const [newRow, setNewRow] = useState({
        bsw_low: '',
        bsw_high: '',
        discount: '',
        disc_percentage: '0', // "0" for Fixed; "1" for Percentage.
	});
      
	const handleAddNewRow = async () => {
		const baseData = {
			main: '0',
			bsw: '1',
			month: activePricing.month,
			year: activePricing.year,
			bsw_low: newRow.bsw_low,
			bsw_high: newRow.bsw_high,
			discount: newRow.discount,
			disc_percentage: newRow.disc_percentage,
		};

        // Add table-specific fields
		const rowData: Record<string, string> = activeTable === 'market_discounts' ? {
			...baseData,
			station_owner: activePricing.station_owner,
			station: activePricing.station,
			market: activePricing.market,
		} : {
			...baseData,
			producer: activePricing.producer,
		};

		try {
			await handleAddRow(activeTable, rowData);
			// Clear the form after adding
			setNewRow({
				bsw_low: '',
				bsw_high: '',
				discount: '',
				disc_percentage: '0',
			});
		} catch (error) {
			console.error('Error adding new bracket row:', error);
		}
	};

	const handleAddLocationPricing = async () => {
		const baseData = {
			main: '0',
			location_pricing: '1',
			month: activePricing.month,
			year: activePricing.year,
			location_type: dropdownSelection,
		};

        // Add table-specific fields
		const rowData: Record<string, string> = activeTable === 'market_discounts' ? {
			...baseData,
			station_owner: activePricing.station_owner,
			station: activePricing.station,
			market: activePricing.market,
			location1: selectedLocation1,
			location2: selectedLocation2,			
		} : {
			...baseData,
			producer: activePricing.producer,
			location: selectedLocation,
		};

		try {
			await handleAddRow(activeTable, rowData);
			// Clear the form after adding
			setSelectedLocation('')
			setSelectedLocation1('')
			setSelectedLocation2('')
		} catch (error) {
			console.error('Error adding new bracket row:', error);
		}
	};
	
	const handleAddSplitStations = async () => {
		try {
			// Get the current split_stations value
			const currentSplitStations = activePricing.split_stations;
			
			// Parse the existing JSON or create empty array if null/undefined
			let stationsArray = [];
			if (currentSplitStations) {
				try {
					stationsArray = JSON.parse(currentSplitStations);
					// Ensure it's an array
					if (!Array.isArray(stationsArray)) {
						stationsArray = [];
					}
				} catch (parseError) {
					console.warn('Error parsing split_stations JSON, starting with empty array:', parseError);
					stationsArray = [];
				}
			}
			
			// Check if the station is already in the array
			if (stationsArray.includes(selectedSplitStation)) {
				console.warn('Station already exists in split_stations');
				return;
			}
			
			// Add the new station
			stationsArray.push(selectedSplitStation);
			
			// Convert back to JSON string
			const newSplitStations = JSON.stringify(stationsArray);
			
			// Call handleEditOrder to save the change
			await handleEditOrder(
				activeTable, 
				activePricing.id, 
				'split_stations', 
				currentSplitStations || '', 
				newSplitStations
			);
			
			// Clear the selected station after adding
			setSelectedSplitStation('');
			
		} catch (error) {
			console.error('Error adding split station:', error);
		}
	};

	// State for new gravity row inputs
	const [newGravityRow, setNewGravityRow] = useState({
		gravity_low: '',
		gravity_high: ''
	});

	const [newNglRow, setNewNglRow] = useState({
		lease: '',
		c1: '',
		c2: '',
		c3: '',
		ic4: '',
		nc4: '',
		c5plus: ''
	});

	useEffect(() => {
		if (activeTable === 'producer_pricing' && !hasTableData('lease_coordinates')) {
			requestTableData('lease_coordinates');
		}
	}, [activeTable]);

	const nglLeaseOptions = useMemo(() => {
		if (!apiData?.lease_coordinates || activeTable !== 'producer_pricing') return [];
		const leases = apiData.lease_coordinates
			.filter((l: LeaseCoordinateItem) => l.producer_id === discount.producer_id)
			.map((l: LeaseCoordinateItem) => l.lease);
		return Array.from(new Set(leases)).sort().map(l => ({ value: l, label: l }));
	}, [apiData?.lease_coordinates, discount.producer, activeTable]);
  
	// Compute gravity rows from allDiscounts
	const gravityRows = useMemo(() => {
		if (!gravEnabled) return [];
		const filterCondition = activeTable === 'market_discounts' 
			? (row: any) => 
				row.station_owner === activePricing.station_owner &&
				row.station === activePricing.station &&
				row.market === activePricing.market &&
				row.month === activePricing.month &&
				row.year === activePricing.year &&
				row.main === 0 &&
				row.gravity === 1
			: activeTable === 'producer_pricing'
			? (row: any) => 
				row.producer === activePricing.producer &&
				row.month === activePricing.month &&
				row.year === activePricing.year &&
				row.main === 0 &&
				row.gravity === 1
			: () => false;
		
		return allDiscounts
			.filter(filterCondition)
			.sort((a, b) => parseFloat(a.gravity_low) - parseFloat(b.gravity_low));
	}, [allDiscounts, activePricing, gravEnabled, activeTable]);

	const nglRows = useMemo(() => {
		if (!nglEnabled) return [];
		const filterCondition = activeTable === 'market_discounts' 
			? (row: any) => 
				row.station_owner === activePricing.station_owner &&
				row.station === activePricing.station &&
				row.market === activePricing.market &&
				row.month === activePricing.month &&
				row.year === activePricing.year &&
				row.main === 0 &&
				row.ngl === 1
			: activeTable === 'producer_pricing'
			? (row: any) => 
				row.producer === activePricing.producer &&
				row.month === activePricing.month &&
				row.year === activePricing.year &&
				row.main === 0 &&
				row.ngl === 1
			: () => false;
		
		return allDiscounts
			.filter(filterCondition)
	}, [allDiscounts, activePricing, nglEnabled, activeTable]);
  
	// Handler to add a new gravity row
	const handleAddGravityRow = async () => {
		const baseData = {
			main: '0',
			gravity: '1',
			month: activePricing.month,
			year: activePricing.year,
			gravity_low: newGravityRow.gravity_low,
			gravity_high: newGravityRow.gravity_high,
		};

		// Add table-specific fields
		const rowData: Record<string, string> = activeTable === 'market_discounts' 
			? {
				...baseData,
				station_owner: activePricing.station_owner,
				station: activePricing.station,
				market: activePricing.market,
			}
			: {
				...baseData,
				producer: activePricing.producer,
			};
			
		try {
			await handleAddRow(activeTable, rowData);
			setNewGravityRow({ gravity_low: '', gravity_high: '' });
		} catch (error) {
			console.error('Error adding gravity row:', error);
		}
	};
	const handleAddNglRow = async () => {
		const baseData = {
			main: '0',
			ngl: '1',
			month: activePricing.month,
			year: activePricing.year,
			c1: newNglRow.c1,
			c2: newNglRow.c2,
			c3: newNglRow.c3,
			ic4: newNglRow.ic4,
			nc4: newNglRow.nc4,
			c5plus: newNglRow.c5plus,
		};

		// Add table-specific fields
		const rowData: Record<string, string> = activeTable === 'market_discounts'
			? {
				...baseData,
				station_owner: activePricing.station_owner,
				station: activePricing.station,
				market: activePricing.market,
			}
			: {
				...baseData,
				producer: activePricing.producer,
				location: newNglRow.lease,
				producer_id: activePricing.producer_id,
			};

		try {
			await handleAddRow(activeTable, rowData);
			setNewNglRow({ lease: '', c1: '', c2: '', c3: '', ic4: '', nc4: '', c5plus: '' });
		} catch (error) {
			console.error('Error adding NGL row:', error);
		}
	};

	return (
		<div className={`pp-discount-details-panel ${isItemActive ? 'active' : 'inactive'}`}>

			<div className='pp-discount-details-panel-top'>
				{activeTable === 'market_discounts' && (
					<div className="pp-station-header">
						<h3>{activePricing.station}</h3>
						<h2>{activePricing.market}</h2>
						<h3>{activePricing.station_owner}</h3>
					</div>
				)}
				{activeTable === 'producer_pricing' && (
					<div className="pp-station-header">
						<h2>{activePricing.producer}</h2>
					</div>
				)}
				<div className="pp-close-button">
					<button onClick={onClose}>
						Close
					</button>
				</div>
			</div>

			<div className="pp-isactive-checkbox">
				<input
					type="checkbox"
					checked={isItemActive}
					onChange={() => handleCheckboxSave('is_active', isItemActive)}
					disabled={isLoading}
				/>
				<h2>Activate Pricing</h2>
			</div>

	 		<div className="pp-month-year-picker">
	 			<label>Time Period:</label>
	 			<select 
	 				value={currentMonthYear} 
					onChange={handleMonthYearChange}
					className='pp-monthyearDropdown'
				>
					{monthYearOptions.map(option => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			</div>
	
        	<div className="pp-base-deduct">
				<label>Base Deduct:</label>
				<input
					className="pp-deduct-input"
					type="text"
					value={deductValue}
					onChange={e => setDeductValue(e.target.value)}
				/>
			</div>
			
			{activeTable == 'market_discounts' && (
				<div className="pp-base-deduct">
					<label>Station Charge:</label>
					<input
						className="pp-deduct-input"
						type="text"
						value={stationCharge}
						onChange={e => setStationCharge(e.target.value)}
					/>
				</div>
			)}

			<div className="pp-dividing-line" />
			
			{activeTable == 'market_discounts' && (

				<div className='pp-horizontal-container'>
					<div className='pp-buyselldeal-container'>
						<div className='pp-horizontal-container'>
							<input
								type="checkbox"
								checked={isBuySellDeal}
								onChange={() => handleCheckboxSave('is_buy_sell_deal', isBuySellDeal)}
								disabled={isLoading}
							/>
							<h2>Buy/Sell Market</h2>
						</div>
					</div>
				</div>
			)}

			{activeTable == 'producer_pricing' && (
				<div>
					<div>
						<div className='pp-horizontal-container'>
							<input
								type="checkbox"
								checked={isRockbridgeTitle}
								onChange={() => handleCheckboxSave('rockbridge_title', isRockbridgeTitle)}
								disabled={isLoading}
							/>
							<h2>Rockbridge Takes Title</h2>
						</div>
						<div className='pp-horizontal-container'>
							<div className='pp-pickupdeal-container'>
								<input
									type="checkbox"
									checked={isPickedUpDeal}
									onChange={() => handleCheckboxSave('is_pickedup_deal', isPickedUpDeal)}
									disabled={isLoading}
								/>
								<h2>Picked-Up Deal</h2>
								{isPickedUpDeal && (
									<div className='pp-horizontal-container'>
										<PanelEditableCell
											width={100}
											height={24}
											initialValue={activePricing.pickedup_with}
											onSave={async (newValue) => {
												if (newValue !== activePricing.pickedup_with) {
													await handleEditOrder(activeTable, activePricing.id, 'pickedup_with', activePricing.pickedup_with, newValue);
												}
											}}
										/>
										<Dropdown
											options={[
												{ value: 'At the lease', label: 'At the lease' },
												{ value: 'At the market', label: 'At the market' }
											]}
											height={24}
											placeholder='Select one'
											value={pickupDealLocation}
											onChange={async (newValue) => {
												if (newValue !== activePricing.pickedup_deal_at) {
													await handleEditOrder(activeTable, activePricing.id, 'pickedup_deal_at', activePricing.pickedup_deal_at, newValue);
												}
											}}
										/>
									</div>
								)}
							</div>
						</div>
						<div className='pp-horizontal-container'>
							<div className='pp-buyselldeal-container'>
								<div className='pp-horizontal-container'>
									<input
										type="checkbox"
										checked={isBuySellDeal}
										onChange={() => handleCheckboxSave('is_buy_sell_deal', isBuySellDeal)}
										disabled={isLoading}
									/>
								
									<h2>Buy/Sell Deal</h2>
								</div>
								<div>
									{isBuySellDeal && (
										<div className='pp-horizontal-container'>
											<PanelEditableCell
												width={60}
												height={24}
												initialValue={activePricing.buy_sell_deduct}
												onSave={async (newValue) => {
													if (newValue !== activePricing.buy_sell_deduct) {
														await handleEditOrder(activeTable, activePricing.id, 'buy_sell_deduct', activePricing.buy_sell_deduct, newValue);
													}
												}}
											/>
											<input
												type="checkbox"
												checked={buySellComponents}
												onChange={() => handleCheckboxSave('buy_sell_components', buySellComponents)}
												disabled={isLoading}
											/>
											<h2>Include components</h2>
										</div>
									)}
								</div>
							</div>
						</div>
						<div className='pp-main-split'>
							<div>
								<div className='pp-horizontal-container'>
									<input
										type="checkbox"
										checked={isSplitDeal}
										onChange={() => handleCheckboxSave('is_split_deal', isSplitDeal)}
										disabled={isLoading}
									/>
									<h2>Split Deal</h2>
								</div>
							</div>
							<div className='pp-split-percentage'>
								{isSplitDeal && (
									<div className='pp-horizontal-container'>
										<PanelEditableCell
											width={100}
											initialValue={activePricing.split_with}
											onSave={async (newValue) => {
												if (newValue !== activePricing.split_with) {
													await handleEditOrder(activeTable, activePricing.id, 'split_with', activePricing.split_with, newValue);
												}
											}}
										/>
										<PanelEditableCell
											width={40}
											initialValue={activePricing.net_split_percentage}
											onSave={async (newValue) => {
												if (newValue !== activePricing.net_split_percentage) {
													await handleEditOrder(activeTable, activePricing.id, 'net_split_percentage', activePricing.net_split_percentage, newValue);
												}
											}}
										/>
										<label>
											: {100 - activePricing.net_split_percentage}
										</label>
										<label>
											Rockbridge
										</label>
									</div>
								)}
							</div>
						</div>
					</div>
					{isSplitDeal && (
						<div>
							<div className='pp-main-split'>
								<div className='pp-horizontal-container'>
									<input
										type="checkbox"
										checked={isSplitTrucking}
										onChange={() => handleCheckboxSave('split_trucking', isSplitTrucking)}
										disabled={isLoading}
									/>
									<h2>Split Trucking</h2>
								</div>
								<div className='pp-split-percentage'>
									{isSplitTrucking && (
										<div className='pp-horizontal-container'>
											<PanelEditableCell
												editable={false}
												width={100}
												initialValue={activePricing.split_with}
												onSave={async (newValue) => {
													if (newValue !== activePricing.split_with) {
														await handleEditOrder(activeTable, activePricing.id, 'split_with', activePricing.split_with, newValue);
													}
												}}
											/>
											<PanelEditableCell
												width={40}
												initialValue={activePricing.split_trucking_percentage}
												onSave={async (newValue) => {
													if (newValue !== activePricing.split_trucking_percentage) {
														await handleEditOrder(activeTable, activePricing.id, 'split_trucking_percentage', activePricing.split_trucking_percentage, newValue);
													}
												}}
											/>
											<label>
												: {100 - activePricing.split_trucking_percentage}
											</label>
											<label>
												Rockbridge
											</label>
										</div>
									)}
								</div>
							</div>
							<div className='pp-horizontal-container'>
								<input
									type="checkbox"
									checked={isSplitAllLeases}
									onChange={() => handleCheckboxSave('split_all_leases', isSplitAllLeases)}
									disabled={isLoading}
								/>
								<h2>At All Leases</h2>
							</div>
							<div className='pp-horizontal-container'>
								<input
									type="checkbox"
									checked={isSplitAllStations}
									onChange={() => handleCheckboxSave('split_all_stations', isSplitAllStations)}
									disabled={isLoading}
								/>
								<h2>At All Stations</h2>
								{!isSplitAllStations && (
									<div>
										<div style={{display: 'flex', flexDirection: 'row'}}>
											<Dropdown
												options={stationOptions}
												placeholder='Select Station'
												value={selectedSplitStation}
												onChange={setSelectedSplitStation}
											/>
											{activeTable == 'producer_pricing' && selectedSplitStation ? (
												<div style={{alignItems: 'center', display: 'flex'}}>
													<button
														onClick={handleAddSplitStations}
														style={{
															backgroundColor: 'green',
															color: 'white',
															height: '20px',
															width: '20px',
															borderRadius: '40px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}
													>
														+
													</button>
												</div>
											):(null)}
										</div>
										<div>{activePricing.split_stations}</div>
									</div>
								)}
							</div>
						</div>						
					)}
				</div>
			)}

			{activeTable === 'producer_pricing' && (
				<div className="pp-dividing-line" />
			)}

			<div className="location-deduct" style={{ marginBottom: '16px' }}>
				<h4 style={{ marginBottom: '5px' }}>Additional Deducts</h4>
				
				<div className="location-deduct2" style={{ marginBottom: '16px' }}>

					{activeTable == 'producer_pricing' && (
						<div>
							<Dropdown
								options={dropdownOptions}
								placeholder='None'
								value={dropdownSelection}
								onChange={(value) => {
									setDropdownSelection(value);
								}}
							/>
							{dropdownSelection === 'lease' ? (
								<Dropdown
									options={leaseOptions}
									placeholder='Select Lease'
									value={selectedLocation}
									onChange={setSelectedLocation}
								/>
							):(null)}
							{dropdownSelection === 'market' ? (
								<Dropdown
									options={marketOptions}
									placeholder='Select Market'
									value={selectedLocation}
									onChange={setSelectedLocation}
								/>
							):(null)}
							{dropdownSelection === 'entity' ? (
								<Dropdown
									options={entityOptions}
									placeholder='Select Entity'
									value={selectedLocation}
									onChange={setSelectedLocation}
								/>
							):(null)}
						</div>
					)}

					{activeTable == 'market_discounts' && (
						<div>
							<Dropdown
								options={dropdownOptions}
								placeholder='None'
								value={dropdownSelection}
								onChange={(value) => {
									setDropdownSelection(value);
								}}
							/>
							{dropdownSelection ? ( 
								<Dropdown
									options={location1Options}
									placeholder='Select'
									value={selectedLocation1}
									onChange={setSelectedLocation1}
								/>
							):(null)}
							{selectedLocation1 && dropdownSelection == 'producer' ? (
								<Dropdown
									options={location2Options}
									placeholder='Select'
									value={selectedLocation2}
									onChange={setSelectedLocation2}
								/>
							):(null)}
						</div>
					)}

					{activeTable == 'producer_pricing' && selectedLocation ? (
						<div style={{alignItems: 'center', display: 'flex'}}>
							<button
								onClick={handleAddLocationPricing}
								style={{
									backgroundColor: 'green',
									color: 'white',
									height: '20px',
									width: '20px',
									borderRadius: '40px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								+
							</button>
						</div>
					):(null)}

					{activeTable == 'market_discounts' && (dropdownSelection == 'offload_lane' || dropdownSelection == 'product_type') && selectedLocation1 ? (
						<div style={{alignItems: 'center', display: 'flex'}}>
							<button
								onClick={handleAddLocationPricing}
								style={{
									backgroundColor: 'green',
									color: 'white',
									height: '20px',
									width: '20px',
									borderRadius: '40px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								+
							</button>
						</div>
					):(null)}

					{activeTable == 'market_discounts' && dropdownSelection == 'producer' && selectedLocation2 ? (
						<div style={{alignItems: 'center', display: 'flex'}}>
							<button
								onClick={handleAddLocationPricing}
								style={{
									backgroundColor: 'green',
									color: 'white',
									height: '20px',
									width: '20px',
									borderRadius: '40px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								+
							</button>
						</div>
					):(null)}

				</div>

				<div>
					{locationPricingRows.length > 0 && (
						<div className="location-pricing-rows">
							<div style={{ maxHeight: '200px', overflowY: 'auto' }}>
							{locationPricingRows.map((row, index) => (
								<div
									key={index}
									style={{
										display: 'flex',
										alignItems: 'center',
										marginBottom: '8px',
										padding: '8px',
										borderRadius: '4px',
										backgroundColor: '#f5f5f5',
									}}
								>
								{activeTable === 'producer_pricing' ? (
									<div style={{ marginRight: 'auto' }}>
										<div>
											<span style={{ fontWeight: 'bold' }}>{row.location}</span>
										</div>
										<div style={{ 
											fontSize: '0.8em', 
											color: '#666',
											textTransform: 'capitalize' 
										}}>
											{row.location_type}
										</div>
									</div>
								) : (
									<div style={{ marginRight: 'auto' }}>
										<div>
											<span style={{ fontWeight: 'normal' }}>{row.location1}</span>
										</div>
										<span style={{ fontWeight: 'bold' }}>{row.location2}</span>
										<div style={{ 
											fontSize: '0.8em', 
											color: '#666',
											textTransform: 'capitalize' 
										}}>
											{row.location_type}
										</div>
									</div>
								)}
								
								<div style={{ 
									display: 'flex',
									alignItems: 'center',
									marginRight: '8px',
								}}>
									<PanelEditableCell
										initialValue={row.location_discount}
										onSave={async (newValue) => {
											if (newValue !== row.location_discount) {
												await handleEditOrder(activeTable, row.id, 'location_discount', row.location_discount, newValue);
											}
										}}
									/>
									
								</div>
								
								<button 
									onClick={async () => {
									try {
										await handleDeleteRow(new Set([row.id]), activeTable);
									} catch (error) {
										console.error('Error deleting location pricing row:', error);
									}
									}}
									style={{
									backgroundColor: 'red',
									borderRadius: '50%',
									width: '24px',
									height: '24px',
									border: 'none',
									color: 'white',
									fontWeight: 'bold',
									cursor: 'pointer'
									}}
								>
									&minus;
								</button>
								</div>
							))}
							</div>
						</div>
						)}
				</div>

			</div>

			<div className="checkbox-section">
				<h4>Pricing Components</h4>
				<div className="checkbox-grid">
					{TICKET_FIELDS.map(field => (
						<label key={field} className="checkbox-label">
							<input
								type="checkbox"
								checked={checkboxValues[field] || false}
								onChange={() => handleCheckboxChange(field)}
								disabled={isLoading}
							/>
							<span>{field.toUpperCase()}</span>
						</label>
					))}
				</div>
			</div>
  	
			<div className="checkbox-section">
				<h4>BS&W Settings</h4>
				<label className="checkbox-label">
					<input
						type="checkbox"
						checked={bswEnabled}
						onChange={handleBswChange}
						disabled={isLoading}
					/>
					<span>Apply BS&W Brackets</span>
				</label>

				{bswEnabled && (
					<div className="brackets-container">
						<h5>BS&W Brackets</h5>
						<div className="new-row-form" style={{ marginBottom: '8px' }}>
							<input 
								type="text" 
								placeholder="BSW Low" 
								value={newRow.bsw_low} 
								onChange={e => setNewRow({ ...newRow, bsw_low: e.target.value })}
								style={{ marginRight: '4px' }}
							/>
							<input 
								type="text" 
								placeholder="BSW High" 
								value={newRow.bsw_high} 
								onChange={e => setNewRow({ ...newRow, bsw_high: e.target.value })}
								style={{ marginRight: '4px' }}
							/>
							<input 
								type="text" 
								placeholder="Deduct" 
								value={newRow.discount} 
								onChange={e => setNewRow({ ...newRow, discount: e.target.value })}
								style={{ marginRight: '4px' }}
							/>
							<Toggle 
								checked={newRow.disc_percentage === '1'} 
								onChange={checked => setNewRow({ 
									...newRow, 
									disc_percentage: checked ? '1' : '0' 
								})}
								labelOn="Percentage"
								labelOff="Fixed"
							/>
							<button
								onClick={handleAddNewRow}
								style={{
									backgroundColor: 'green',
									color: 'white',
									padding: '4px 8px',
									border: 'none',
									borderRadius: '4px',
									cursor: 'pointer',
									marginLeft: '8px'
								}}
							>
								Add Row
							</button>
						</div>
						{bswBrackets.length > 0 && ( 
							<table className="brackets-table">
								<thead>
									<tr>
										<th>BSW Low (%)</th>
										<th>BSW High (%)</th>
										<th>Deduct</th>
										<th>Deduct Type</th>
									</tr>
								</thead>
								<tbody>
									{bswBrackets.map((bracket, index) => (
										<tr key={bracket.id || index}>
											<td>
												<PanelEditableCell
													initialValue={bracket.bsw_low.toString()}
													onSave={newValue => handleEditBracket(bracket, 'bsw_low', newValue)}
												/>
											</td>
											<td>
												<PanelEditableCell
													initialValue={bracket.bsw_high.toString()}
													onSave={newValue => handleEditBracket(bracket, 'bsw_high', newValue)}
												/>
											</td>
											<td>
												<DeductValueCell bracket={bracket} />
											</td>
											<td>
												<DeductTypeCell bracket={bracket} />
											</td>
											<td>
												<button 
													onClick={async () => {
														try {
															await handleDeleteRow(new Set([bracket.id]), activeTable);
														} catch (error) {
															console.error('Error deleting bracket:', error);
														}
													}}
													style={{
														backgroundColor: 'red',
														borderRadius: '50%',
														width: '24px',
														height: '24px',
														border: 'none',
														color: 'white',
														fontWeight: 'bold',
														cursor: 'pointer'
													}}
												>
													&minus;
												</button>
											</td>
										</tr>                  
									))}
								</tbody>
							</table>
						)}
					</div>
				)}
			</div>
  	
        	<div className="checkbox-section">
          		<h4>Gravity Settings</h4>
          		<label className="checkbox-label">
					<input
						type="checkbox"
						checked={gravEnabled}
						onChange={handleGravChange}
						disabled={isLoading}
					/>
            		<span>Apply Gravity Brackets</span>
          		</label>
          		{gravEnabled && (
  					<div className="gravity-container" style={{ marginTop: '16px' }}>
    					<h5>Gravity Brackets</h5>

						<div className="new-row-form" style={{ marginBottom: '8px' }}>
							<input 
								type="text" 
								placeholder="Gravity Low" 
								value={newGravityRow.gravity_low} 
								onChange={e => setNewGravityRow({ ...newGravityRow, gravity_low: e.target.value })}
								style={{ marginRight: '4px' }}
							/>
							<input 
								type="text" 
								placeholder="Gravity High" 
								value={newGravityRow.gravity_high} 
								onChange={e => setNewGravityRow({ ...newGravityRow, gravity_high: e.target.value })}
								style={{ marginRight: '4px' }}
							/>
							<button
								onClick={handleAddGravityRow}
								style={{
									backgroundColor: 'green',
									color: 'white',
									padding: '4px 8px',
									border: 'none',
									borderRadius: '4px',
									cursor: 'pointer'
								}}
							>
								+ Add Gravity Row
							</button>
						</div>

						{gravityRows.length > 0 && (
							<table className="brackets-table">
								<thead>
									<tr>
										<th>Gravity Low (%)</th>
										<th>Gravity High (%)</th>
										<th>Deduct</th>
										<th>Deduct Type</th>
									</tr>
								</thead>
								<tbody>
									{gravityRows.map((row, index) => (
										<tr key={index}>
											<td>
												<PanelEditableCell
													initialValue={row.gravity_low?.toString() || ""}
													onSave={newValue => handleEditBracket(row, 'gravity_low', newValue)}
												/>
											</td>
											<td>
												<PanelEditableCell
													initialValue={row.gravity_high?.toString() || ""}
													onSave={newValue => handleEditBracket(row, 'gravity_high', newValue)}
												/>
              								</td>
											<td>
												<DeductValueCell bracket={row} />
											</td>
											<td>
												<DeductTypeCell bracket={row} />
											</td>
											<td>
												<button 
													onClick={async () => {
														try {
															await handleDeleteRow(new Set([row.id]), activeTable);
														} catch (error) {
															console.error('Error deleting gravity row:', error);
														}
													}}
													style={{
														backgroundColor: 'red',
														borderRadius: '50%',
														width: '24px',
														height: '24px',
														border: 'none',
														color: 'white',
														fontWeight: 'bold',
														cursor: 'pointer'
													}}
                								>
													&minus;
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				)}
        	</div>
			<div className="checkbox-section">
          		<h4>NGL Settings</h4>
          		<label className="checkbox-label">
					<input
						type="checkbox"
						checked={nglEnabled}
						onChange={handleNglChange}
						disabled={isLoading}
					/>
            		<span>Apply NGL Settings</span>
          		</label>
          		{nglEnabled && (
  					<div className="gravity-container" style={{ marginTop: '16px' }}>
    					<h5>NGL Brackets</h5>
						{nglRows.length > 0 && (
							<div style={{ marginBottom: '12px' }}>
								{nglRows.map((row, index) => (
									<div key={index} style={{ marginBottom: '12px', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
										{row.location && (
											<div style={{ fontWeight: 700, fontSize: '0.9em', marginBottom: '6px' }}>
												{row.location}
											</div>
										)}

										{([
											
											{ field: 'c1', label: 'C1' },
											{ field: 'c2', label: 'C2' },
											{ field: 'c3', label: 'C3' },
											{ field: 'ic4', label: 'IC4' },
											{ field: 'nc4', label: 'NC4' },
											{ field: 'c5plus', label: 'C5+' },
										] as const).map(({ field, label }) => (
											<div key={field} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
												<span style={{ width: '48px', fontWeight: 600, fontSize: '0.85em' }}>{label}</span>
												<PanelEditableCell
													initialValue={row[field]?.toString() || ""}
													onSave={newValue => handleEditBracket(row, field, newValue)}
												/>
											</div>
										))}
										<button
											onClick={async () => {
												try {
													await handleDeleteRow(new Set([row.id]), activeTable);
												} catch (error) {
													console.error('Error deleting NGL row:', error);
												}
											}}
											style={{
												backgroundColor: 'red',
												borderRadius: '50%',
												width: '24px',
												height: '24px',
												border: 'none',
												color: 'white',
												fontWeight: 'bold',
												cursor: 'pointer',
												marginTop: '4px',
											}}
										>
											&minus;
										</button>
									</div>
								))}
							</div>
						)}
						<div className="new-row-form" style={{ marginBottom: '8px' }}>
							{activeTable === 'producer_pricing' && (
								<select
									value={newNglRow.lease}
									onChange={e => setNewNglRow({ ...newNglRow, lease: e.target.value })}
									style={{ marginRight: '4px', marginBottom: '4px', width: '140px' }}
								>
									<option value="">Select Lease</option>
									{nglLeaseOptions.map(opt => (
										<option key={opt.value} value={opt.value}>{opt.label}</option>
									))}
								</select>
							)}
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
								{(['c1', 'c2', 'c3', 'ic4', 'nc4', 'c5plus'] as const).map(field => (
									<input
										key={field}
										type="text"
										placeholder={field === 'c5plus' ? 'C5+' : field.toUpperCase()}
										value={newNglRow[field]}
										onChange={e => setNewNglRow({ ...newNglRow, [field]: e.target.value })}
										style={{ width: '60px' }}
									/>
								))}
							</div>
							<button
								onClick={handleAddNglRow}
								disabled={
									(['c1', 'c2', 'c3', 'ic4', 'nc4', 'c5plus'] as const).some(f => newNglRow[f].trim() === '') ||
									(activeTable === 'producer_pricing' && newNglRow.lease.trim() === '')
								}
								style={{
									backgroundColor:
										(['c1', 'c2', 'c3', 'ic4', 'nc4', 'c5plus'] as const).some(f => newNglRow[f].trim() === '') ||
										(activeTable === 'producer_pricing' && newNglRow.lease.trim() === '')
											? '#aaa' : 'green',
									color: 'white',
									padding: '4px 8px',
									border: 'none',
									borderRadius: '4px',
									cursor:
										(['c1', 'c2', 'c3', 'ic4', 'nc4', 'c5plus'] as const).some(f => newNglRow[f].trim() === '') ||
										(activeTable === 'producer_pricing' && newNglRow.lease.trim() === '')
											? 'not-allowed' : 'pointer',
									marginTop: '6px',
								}}
							>
								+ Add NGL Data
							</button>
						</div>
					</div>
				)}
        	</div>
			
			<ConfirmDialog
				isOpen={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
				title="Confirm Deletion"
				message={`Are you sure you want to delete all data for ${activePricing?.month || ''} ${activePricing?.year || ''}? This action cannot be undone.`}
				onConfirm={confirmDelete}
				button1="Cancel"
				showbutton2={true}
				button2="Delete"
			/>

			<div className="pp-dividing-line" />
			
         	<div className="panel-footer">
			 	<button onClick={handleDeleteClick} className="delete-button" disabled={isLoading}>
	 				Delete Month
				</button>

	 			<button onClick={handleSave} className="save-button" disabled={isLoading}>
	 				{isLoading ? 'Saving...' : 'Save Changes'}
				</button>
	 		</div> 
      	</div>
    );
};
  
export default DiscountDetailsPanel;